import React, { useState, useEffect, useRef } from 'react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../../../context/LanguageContext';
import { fetchTrack } from '../../../utils/gpsTrackDb';
import { getTrack } from '../../../utils/gpsTrack';
import { computeStats, getPresetRange, fmtDuration } from '../../../utils/vehicleStats';

const PRESETS = [
  { k: 'today', fr: "Aujourd'hui", ar: 'اليوم' },
  { k: 'yesterday', fr: 'Hier', ar: 'أمس' },
  { k: 'last2', fr: '2 derniers jours', ar: 'آخر يومين' },
  { k: 'last3', fr: '3 derniers jours', ar: 'آخر 3 أيام' },
  { k: 'week', fr: 'Cette semaine', ar: 'هذا الأسبوع' },
  { k: 'lastweek', fr: 'Semaine dernière', ar: 'الأسبوع الماضي' },
  { k: 'month', fr: 'Ce mois', ar: 'هذا الشهر' },
  { k: 'lastmonth', fr: 'Mois dernier', ar: 'الشهر الماضي' },
  { k: 'custom', fr: 'Personnalisé', ar: 'مخصص' },
];

const toLocalInput = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function VehicleStatsModal({ car, settings, agencyId, onClose }) {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const t = (fr, ar) => (isAr ? ar : fr);
  const cur = settings?.currency || 'MAD';
  const speedLimit = settings?.gpsSpeedLimit ?? 120;

  const [rangeKey, setRangeKey] = useState('today');
  const [from, setFrom] = useState(toLocalInput(new Date(Date.now() - 86400000)));
  const [to, setTo] = useState(toLocalInput(new Date()));
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const cacheRef = useRef({});
  const reportRef = useRef(null);
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  const fmtTime = (ms) => (ms ? new Date(ms).toLocaleString(isAr ? 'ar-MA' : 'fr-FR') : '—');

  const load = async (sinceMs, untilMs) => {
    if (!car) return;
    const key = `${car.id}_${sinceMs}_${untilMs}`;
    setLoading(true);
    try {
      let pts = cacheRef.current[key];
      if (!pts) {
        const remote = await fetchTrack({ agencyId, vehicleId: car.id, sinceMs, untilMs });
        pts = remote.length > 0 ? remote : getTrack(car.id, sinceMs, untilMs);
        cacheRef.current[key] = pts;
      }
      setStats(computeStats(pts, { speedLimitKph: speedLimit, car, settings }));
    } catch (_) {
      setStats(computeStats([], {}));
    } finally {
      setLoading(false);
    }
  };

  // Load when the preset range changes.
  useEffect(() => {
    if (rangeKey === 'custom') return;
    const { sinceMs, untilMs } = getPresetRange(rangeKey);
    load(sinceMs, untilMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey, car]);

  const applyCustom = () => {
    const s = new Date(from).getTime();
    const u = new Date(to).getTime();
    if (isNaN(s) || isNaN(u) || s >= u) return;
    setRangeKey('custom');
    load(s, u);
  };

  // Init map once
  useEffect(() => {
    const L = window.L;
    if (!L || !mapElRef.current || mapRef.current) return;
    const map = L.map(mapElRef.current, { zoomControl: false, attributionControl: false }).setView([31.7, -7], 5);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
    mapRef.current = map;
    setTimeout(() => { try { map.invalidateSize(); } catch (_) {} }, 200);
    return () => { try { map.remove(); } catch (_) {} mapRef.current = null; };
  }, []);

  // Draw route + markers when stats change
  useEffect(() => {
    const L = window.L;
    const map = mapRef.current;
    if (!L || !map) return;
    if (layerRef.current) { layerRef.current.remove(); layerRef.current = null; }
    if (!stats || !stats.hasData) return;
    const g = L.layerGroup().addTo(map);
    if (stats.path.length >= 2) {
      L.polyline(stats.path, { color: '#2563eb', weight: 4, opacity: 0.85 }).addTo(g);
    }
    // stops (amber) with duration popup
    stats.stops.forEach(s => {
      L.circleMarker([s.lat, s.lng], { radius: 6, color: '#d97706', fillColor: '#f59e0b', fillOpacity: 1, weight: 2 })
        .bindPopup(`${t('Arrêt', 'توقف')}: ${fmtDuration(s.end - s.start)}`).addTo(g);
    });
    // overspeed (red)
    stats.overspeed.forEach(o => {
      L.circleMarker([o.lat, o.lng], { radius: 5, color: '#dc2626', fillColor: '#ef4444', fillOpacity: 1, weight: 2 })
        .bindPopup(`${t('Excès', 'تجاوز')}: ${Math.round(o.speed)} kph`).addTo(g);
    });
    // start / end
    if (stats.path.length) {
      L.circleMarker(stats.path[0], { radius: 7, color: '#16a34a', fillColor: '#22c55e', fillOpacity: 1, weight: 2 }).bindPopup(t('Départ', 'البداية')).addTo(g);
      L.circleMarker(stats.path[stats.path.length - 1], { radius: 7, color: '#dc2626', fillColor: '#ef4444', fillOpacity: 1, weight: 2 }).bindPopup(t('Arrivée', 'النهاية')).addTo(g);
      try { map.fitBounds(L.latLngBounds(stats.path), { padding: [30, 30], maxZoom: 15 }); } catch (_) {}
    }
    layerRef.current = g;
  }, [stats]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build a clean, fixed-width A4 report (own markup) so the PDF/print never
  // overlaps, clips or misaligns like a screenshot of the responsive modal would.
  const esc = (v) => String(v == null ? '' : v).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  const buildReportHtml = (st) => {
    const periodLabel = (PRESETS.find(p => p.k === rangeKey) || {})[isAr ? 'ar' : 'fr'] || '';
    const rows = kpis.map(k => ({ label: k.label, value: k.value }));
    const cells = rows.map(r => `
      <td style="width:26%;padding:7px 8px;border:1px solid #e5e7eb;background:#f8fafc;color:#475569;font-size:11px;vertical-align:top;word-break:break-word;">${esc(r.label)}</td>
      <td style="width:24%;padding:7px 8px;border:1px solid #e5e7eb;color:#0f172a;font-size:12px;font-weight:bold;vertical-align:top;word-break:break-word;">${esc(r.value)}</td>`);
    let tableRows = '';
    for (let i = 0; i < cells.length; i += 2) {
      tableRows += `<tr>${cells[i] || ''}${cells[i + 1] || '<td colspan="2" style="border:1px solid #e5e7eb;"></td>'}</tr>`;
    }
    return `
    <div style="width:760px;margin:0 auto;padding:20px;box-sizing:border-box;background:#ffffff;font-family:Arial,Helvetica,sans-serif;color:#0f172a;">
      <div style="border-bottom:2px solid #2563eb;padding-bottom:10px;margin-bottom:14px;">
        <div style="font-size:18px;font-weight:bold;">${esc(settings?.companyName || 'GPS Tracking')}</div>
        <div style="font-size:13px;color:#475569;">${t('Rapport statistiques du véhicule', 'تقرير إحصائيات السيارة')}</div>
      </div>
      <table style="width:100%;border-collapse:collapse;table-layout:fixed;margin-bottom:14px;font-size:12px;">
        <tr>
          <td style="padding:5px 8px;color:#475569;width:22%;">${t('Véhicule', 'السيارة')}</td>
          <td style="padding:5px 8px;font-weight:bold;width:45%;word-break:break-word;">${esc(car.brand)} ${esc(car.model)}</td>
          <td style="padding:5px 8px;color:#475569;width:15%;">${t('Plaque', 'اللوحة')}</td>
          <td style="padding:5px 8px;font-weight:bold;width:18%;word-break:break-word;">${esc(car.plate || '-')}</td>
        </tr>
        <tr>
          <td style="padding:5px 8px;color:#475569;">${t('Période', 'الفترة')}</td>
          <td style="padding:5px 8px;font-weight:bold;" colspan="3">${esc(periodLabel)} &nbsp;·&nbsp; ${esc(fmtTime(st.startTime))} → ${esc(fmtTime(st.endTime))}</td>
        </tr>
      </table>
      <table style="width:100%;border-collapse:collapse;table-layout:fixed;">${tableRows}</table>
      <div style="margin-top:10px;font-size:10px;color:#94a3b8;font-style:italic;">
        ${t('Ralenti / heures moteur / carburant sont estimés (pas de capteur d\'allumage/carburant).', 'مدة التوقف بالمحرك / ساعات التشغيل / الوقود تقديرية.')}
      </div>
      <div style="margin-top:6px;font-size:10px;color:#94a3b8;">${t('Généré le', 'تم الإنشاء في')} ${esc(new Date().toLocaleString(isAr ? 'ar-MA' : 'fr-FR'))}</div>
    </div>`;
  };

  const exportPdf = () => {
    if (!s || !s.hasData) return;

    // Render the report inside a FULLY VISIBLE white overlay on top of the page.
    // html2canvas frequently produces a blank PDF when its target is off-screen,
    // behind a negative z-index, or transparent — so we capture it while it is
    // genuinely visible, then remove the overlay immediately after.
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;background:#ffffff;z-index:2147483647;overflow:auto;display:flex;justify-content:center;align-items:flex-start;';
    const el = document.createElement('div');
    el.style.cssText = 'width:780px;background:#ffffff;';
    el.innerHTML = buildReportHtml(s);
    overlay.appendChild(el);
    document.body.appendChild(overlay);

    const cleanup = () => { try { document.body.removeChild(overlay); } catch (_) {} };

    // Wait for layout/fonts to settle before capturing.
    setTimeout(() => {
      html2pdf().set({
        margin: [10, 10, 12, 10],
        filename: `stats_${car.plate || car.id}_${rangeKey}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          scrollX: 0,
          scrollY: -window.scrollY, // correct for current page scroll → avoids blank captures
          windowWidth: 820
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] }
      }).from(el).save().then(cleanup, cleanup);
    }, 150);
  };

  const printReport = () => {
    if (!s || !s.hasData) return;
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html dir="${isAr ? 'rtl' : 'ltr'}"><head><meta charset="utf-8"><title>${esc(car.plate || 'rapport')}</title></head><body style="margin:0;">${buildReportHtml(s)}</body></html>`);
    doc.close();
    const go = () => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (_) {} setTimeout(() => { try { document.body.removeChild(iframe); } catch (_) {} }, 1500); };
    setTimeout(go, 400);
  };

  const s = stats;
  const kpis = s && s.hasData ? [
    { icon: '🏁', label: t('Début', 'البداية'), value: fmtTime(s.startTime) },
    { icon: '🏆', label: t('Fin', 'النهاية'), value: fmtTime(s.endTime) },
    { icon: '📏', label: t('Distance', 'المسافة'), value: `${s.distanceKm.toFixed(1)} km` },
    { icon: '🚗', label: t('Conduite', 'مدة السير'), value: fmtDuration(s.movingMs) },
    { icon: '⏸️', label: t('Arrêt', 'مدة التوقف'), value: fmtDuration(s.stopMs) },
    { icon: '📍', label: t('Nb arrêts', 'عدد التوقفات'), value: s.stopsCount },
    { icon: '💨', label: t('Vitesse max', 'أقصى سرعة'), value: `${s.maxSpeed} kph` },
    { icon: '📊', label: t('Vitesse moy.', 'متوسط السرعة'), value: `${s.avgSpeed.toFixed(0)} kph` },
    { icon: '🚨', label: t('Excès vitesse', 'تجاوزات السرعة'), value: s.overspeedCount },
    { icon: '🟡', label: t('Ralenti (est.)', 'توقف بالمحرك (تقديري)'), value: fmtDuration(s.idleMs) },
    { icon: '🟢', label: t('Travail (est.)', 'مدة العمل (تقديري)'), value: fmtDuration(s.workingMs) },
    { icon: '🔢', label: t('Odomètre', 'العداد'), value: `${Math.round(Number(car.gpsOdometer) || Number(car.km) || 0).toLocaleString()} km` },
    { icon: '⏱️', label: t('Heures moteur (est.)', 'ساعات التشغيل (تقديري)'), value: fmtDuration(s.engineMs) },
    { icon: '⛽', label: t('Carburant', 'الوقود'), value: s.fuelLiters != null ? `${s.fuelLiters.toFixed(1)} L` : t('—', '—') },
    { icon: '💰', label: t('Coût carburant', 'تكلفة الوقود'), value: s.fuelCost != null ? `${s.fuelCost.toFixed(0)} ${cur}` : t('non configuré', 'غير مهيّأ') },
  ] : [];

  return (
    <div className="fixed inset-0 z-[10000] bg-black/40 flex items-stretch sm:items-center justify-center sm:p-4" onClick={onClose} dir={isAr ? 'rtl' : 'ltr'}>
      <div
        className="bg-gray-50 w-full sm:max-w-3xl sm:rounded-2xl shadow-2xl flex flex-col max-h-screen sm:max-h-[92vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <div className="text-white font-bold text-base truncate">📊 {car.brand} {car.model}</div>
            <div className="text-blue-100 text-xs">{car.plate}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportPdf} className="bg-white/15 hover:bg-white/25 text-white text-xs font-bold px-3 py-1.5 rounded-lg">📄 PDF</button>
            <button onClick={printReport} className="bg-white/15 hover:bg-white/25 text-white text-xs font-bold px-3 py-1.5 rounded-lg">🖨️</button>
            <button onClick={onClose} className="bg-white/15 hover:bg-white/25 text-white p-1.5 rounded-lg" aria-label="Close">✕</button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-3 py-2 bg-white border-b border-gray-200 shrink-0">
          <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-1">
            {PRESETS.map(p => (
              <button
                key={p.k}
                onClick={() => setRangeKey(p.k)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap border transition ${
                  rangeKey === p.k ? 'bg-blue-600 text-white border-blue-700' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {isAr ? p.ar : p.fr}
              </button>
            ))}
          </div>
          {rangeKey === 'custom' && (
            <div className="flex flex-wrap items-end gap-2 mt-2">
              <label className="text-[10px] font-bold text-gray-500">{t('De', 'من')}
                <input type="datetime-local" value={from} onChange={e => setFrom(e.target.value)} className="block mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-xs" />
              </label>
              <label className="text-[10px] font-bold text-gray-500">{t('À', 'إلى')}
                <input type="datetime-local" value={to} onChange={e => setTo(e.target.value)} className="block mt-0.5 px-2 py-1.5 border border-gray-300 rounded-lg text-xs" />
              </label>
              <button onClick={applyCustom} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-600 text-white">{t('Appliquer', 'تطبيق')}</button>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-3" ref={reportRef}>
          {loading ? (
            <div className="py-16 text-center text-sm text-gray-500">{t('Chargement…', 'جاري التحميل…')}</div>
          ) : !s || !s.hasData ? (
            <div className="py-16 text-center text-sm text-gray-400 italic">
              {t('Aucune donnée GPS pour cette période.', 'لا توجد بيانات GPS لهذه الفترة.')}
            </div>
          ) : (
            <>
              {/* KPI cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                {kpis.map((k, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-xl p-2.5">
                    <div className="text-[10px] text-gray-400 font-bold uppercase flex items-center gap-1">
                      <span>{k.icon}</span><span className="truncate">{k.label}</span>
                    </div>
                    <div className="text-sm font-black text-gray-900 mt-0.5 truncate">{k.value}</div>
                  </div>
                ))}
              </div>

              {/* Map */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div ref={mapElRef} className="w-full h-[220px] sm:h-[300px] bg-gray-100" />
                <div className="flex items-center gap-3 text-[10px] text-gray-500 px-3 py-2 flex-wrap">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />{t('Départ', 'البداية')}</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />{t('Arrivée', 'النهاية')}</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />{t('Arrêts', 'التوقفات')}</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-600" />{t('Trajet', 'المسار')}</span>
                </div>
              </div>

              <p className="text-[10px] text-gray-400 italic mt-2">
                {t('Ralenti / heures moteur / carburant sont estimés (pas de capteur d\'allumage/carburant sur l\'appareil).',
                   'مدة التوقف بالمحرك / ساعات التشغيل / الوقود تقديرية (لا يوجد حسّاس تشغيل/وقود في الجهاز).')}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
